import React, { useEffect, useState } from 'react'
import { Typography, Card, Button, Empty, Input, Spin, Row, Col, Space, Pagination } from "antd";
import { useMyContext } from 'Context/MyContextProvider';
import apiClient from 'services/apiClient';
import { SearchOutlined } from '@ant-design/icons';
import Flex from 'components/shared-components/Flex';
import { useDebounce } from 'hooks/useDebounce';
import { UserSelectField } from '../CustomUtils.js/CommonFieldUtils';
import moment from 'moment';
 
const {Text } = Typography;
const Logs = () => {
    const { api, userRole, authToken, navigate } = useMyContext();
    const [loading, setLoading] = useState(false)
    const [searchText, setSearchText] = useState('');
    const [logs, setLogs] = useState([]);
    const [userId, setUserId] = useState(null);
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(15);
    const [total, setTotal] = useState(0);

    const debouncedSearch = useDebounce(searchText, 500);
 
    useEffect(() => {
        if (userRole !== 'Admin') {
            navigate('404')
        }
       
    }, [userRole]);

    // Reset to first page whenever the (debounced) search changes
    useEffect(() => {
        setPage(1);
    }, [debouncedSearch]);
 
 
    const GetSettingData = async () => {
        setLoading(true)
        try {
            const response = await apiClient.get(`${api}logs`,
                {
                    headers: {
                        'Authorization': 'Bearer ' + authToken,
                    },
                    params: {
                        page,
                        per_page: pageSize,
                        search: debouncedSearch || undefined,
                        user_id: userId || undefined,
                    },
                });
            const data = response.data;
            setLogs(Array.isArray(data?.logs) ? data.logs : []);
            setTotal(data?.pagination?.total || 0);
            setLoading(false)
        } catch (error) {
            setLoading(false)
            // console.log(error);
        }
    }
 
    const handleClearLogs = async () => {
        try {
            await apiClient.delete(`${api}clear-logs`,
                {
                    headers: {
                        'Authorization': 'Bearer ' + authToken,
                    }
                });
            setPage(1);
            GetSettingData()
        } catch (error) {
            // console.log(error);
        }
    }
   
    useEffect(() => {
        GetSettingData()
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [page, pageSize, debouncedSearch, userId])

    const handlePaginationChange = (nextPage, nextPageSize) => {
        setPage(nextPage);
        setPageSize(nextPageSize);
    };

    const handleUserChange = (value) => {
        setUserId(value || null);
        setPage(1);
    };
   
    return (
        <>
            <Card>
                <Typography>
                    <Row gutter={[16, 16]} align="middle" className="mb-3">
                        <Col xs={24} sm={24} md={12} lg={12} xl={12}>
                            <h4 style={{ margin: 0 }}>System Webhook Logs</h4>
                        </Col>
                        <Col xs={24} sm={24} md={12} lg={12} xl={12}>
                            <Space
                                direction="vertical"
                                size="middle"
                                style={{ width: '100% ' }}
                                className="responsive-controls"
                            >
                                <Row gutter={[8, 8]} justify="end">
                                    <Col xs={24} sm={12} md={10} lg={8} xl={8}>
                                        <UserSelectField
                                            value={userId}
                                            HandleUserChange={handleUserChange}
                                        />
                                    </Col>
                                    <Col xs={24} sm={16} md={14} lg={12} xl={8}>
                                        <Input
                                            placeholder="Search logs..."
                                            prefix={<SearchOutlined />}
                                            value={searchText}
                                            onChange={e => setSearchText(e.target.value)}
                                            allowClear
                                        />
                                    </Col>
                                    <Col xs={24} sm={8} md={6} lg={6} xl={6}>
                                        <Button
                                            type="primary"
                                            danger
                                            onClick={() => handleClearLogs()}
                                            block
                                        >
                                            Clear Logs
                                        </Button>
                                    </Col>
                                </Row>
                            </Space>
                        </Col>
                    </Row>
                </Typography>
                {loading ? (
                    <div style={{ textAlign: 'center', padding: '20px' }}>
                        <Spin />
                    </div>
                ) : logs?.length === 0 ? (
                    <Empty description={debouncedSearch ? "No matching logs found" : "No logs available"} />
                ) : (
                    <>
                        {logs?.map((item, index) => {
                            const logEntry = item?.logs && typeof item.logs === 'object' ? item.logs : { value: item?.logs };
                            return (
                                <Typography key={item?.id ?? index} className="mb-4">
                                    <div className="log-container" style={{ position: 'relative', borderRadius: '4px' }}>
                                        <Flex justifyContent='flex-end' style={{ position: 'absolute', top: '10px', right: '10px', zIndex: 1 }}>
                                            <Text
                                                copyable={{
                                                    text: JSON.stringify(item?.logs, null, 2),
                                                    tooltips: ['Copy log', 'Log copied!'],
                                                }}
                                                style={{ fontSize: '16px' }}
                                            />
                                        </Flex>
                                        <pre className='log-object' style={{ maxHeight: '20rem', padding: '15px', overflow: 'auto' }}>
                                            {(item?.company_name || item?.display_phone_number || item?.created_at) && (
                                                <div style={{ marginBottom: 6 }}>
                                                    <span style={{ fontWeight: 600 }}>{item?.company_name || 'Unknown'}</span>
                                                    {item?.display_phone_number && (
                                                        <span style={{ color: '#9ca3af', marginLeft: 6 }}>
                                                            ({item.display_phone_number})
                                                        </span>
                                                    )}
                                                    {item?.created_at && (
                                                        <span style={{ color: '#6b7280', marginLeft: 12, fontSize: '12px' }}>
                                                            {moment(item.created_at).format('YYYY-MM-DD HH:mm:ss')}
                                                        </span>
                                                    )}
                                                </div>
                                            )}
                                            {Object?.entries(logEntry)?.map(([key, value]) => (
                                                <div key={key}>
                                                    <span>{key}: </span>
                                                    <span>{JSON.stringify(value, null, 2)}</span>
                                                </div>
                                            ))}
                                        </pre>
                                    </div>
                                </Typography>
                            );
                        })}
                        <Flex justifyContent="flex-end" className="mt-3">
                            <Pagination
                                current={page}
                                pageSize={pageSize}
                                total={total}
                                showSizeChanger
                                pageSizeOptions={['15', '25', '50', '100']}
                                onChange={handlePaginationChange}
                                showTotal={(t) => `Total ${t} logs`}
                            />
                        </Flex>
                    </>
                )}
            </Card>
        </>
    )
}
 
export default Logs
 